import { ProductAttribute } from "@wttb/product-configurator";
import { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { useStoreProduct } from "@/features/product/contexts/product-context";
import { formatPrice } from "@/features/product/hooks/use-price";
import { CustomQuantityForm } from "./custom-quantity-form";
import { PriceItem, QuantityTable } from "./quantity-table";
import { getPricesDifference, MAX_QUANTITY, MIN_QUANTITY } from "./utils";

export function QuantityAttribute({ attribute }: { attribute: ProductAttribute }) {
  const {
    pricingResult,
    config,
    updateLineItemId,
    selectedOptions,
    setCustomConfigValue,
    setConfigValue,
    setSkipPriceCalculation,
  } = useStoreProduct();

  const methods = useForm<{ quantity: number }>({ mode: "onChange" });
  const maxValue = attribute.max ?? MAX_QUANTITY;
  const minValue = attribute.min ?? MIN_QUANTITY;

  // Custom handler for Quantity + SizeRun attributes combination
  const sizeRunQuantityHandler = (quantity: number) => {
    if (!config?.sizeRun) return;
    const values = Object.values(config.sizeRun).filter(
      (n) => n !== undefined && !isNaN(n),
    ) as number[];
    const sizeRunQty = values.reduce((acc, d) => acc + d, 0);

    if (sizeRunQty < quantity || sizeRunQty > quantity) {
      setSkipPriceCalculation(true);
    }
  };

  const setQuantity = (valueId: string, value: number) => {
    sizeRunQuantityHandler(value);
    setConfigValue("quantity", valueId);
  };

  const setCustomQuantity = (quantity: number) => {
    if (quantity <= maxValue && quantity >= minValue) {
      sizeRunQuantityHandler(quantity);
      setCustomConfigValue("quantity", quantity);
    }
  };

  const prices = useMemo<PriceItem[]>(() => {
    if (!pricingResult?.quantityPrices?.length) return [];
    return pricingResult.quantityPrices.map((price) => {
      const difference = getPricesDifference(pricingResult.salePrice.total, price.salePrice.total);
      const discountDiff = getPricesDifference(price.salePrice.total, price.listPrice.total);

      return {
        id: price.valueId,
        value: price.value,
        unitPrice: formatPrice(price.salePrice.unit, "en", {
          minimumFractionDigits: 2, // not less than 0.001
          maximumFractionDigits: 3,
        }),
        totalPrice: formatPrice(price.listPrice.total),
        discountTotalPrice: discountDiff ? formatPrice(price.salePrice.total) : "",
        priceIncrease: formatPrice(difference),
      };
    });
  }, [pricingResult?.quantityPrices, pricingResult?.price]);

  useEffect(() => {
    // preset if quantity is custom while updating line item
    if (!prices.length) return;
    if (!(updateLineItemId || selectedOptions?.quantity) || !prices?.length || !config?.quantity)
      return;
    const lineItemQuantity = config.quantity || selectedOptions.quantity;
    if (typeof lineItemQuantity !== "number") return;
    const isCustomQuantity = !prices.find((item) => item.value === lineItemQuantity);
    if (isCustomQuantity) {
      methods.setValue("quantity", +lineItemQuantity);
    } else {
      methods.resetField("quantity");
    }
  }, [prices, config?.quantity, updateLineItemId, selectedOptions?.quantity]);

  if (!prices.length) return null;

  return (
    <div className="text-grey-80">
      <FormProvider {...methods}>
        {attribute.custom && (
          <CustomQuantityForm
            setQuantity={setCustomQuantity}
            max={maxValue}
            min={minValue}
            className="mb-[26px] md:mb-10"
          />
        )}
        <QuantityTable
          attribute={attribute}
          prices={prices}
          setQuantity={setQuantity}
          max={maxValue}
          min={minValue}
        />
      </FormProvider>
    </div>
  );
}
